import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload,
  X,
  Loader2,
  Check,
  GripVertical,
  DollarSign,
  Gift,
  Trash2,
  Plus,
} from 'lucide-react';
import { Input } from './input';

/**
 * MultiFileUpload component for uploading multiple audio files to Cloudinary
 * Supports individual track pricing and free toggles
 */
export default function MultiFileUpload({
  tracks = [],
  onChange,
  folder = 'music/audio',
  maxSizeMB = 100,
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

  const uploadToCloudinary = (file, trackId) => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', uploadPreset);
      formData.append('folder', folder);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const pct = Math.round((event.loaded / event.total) * 100);
          setUploadProgress((prev) => ({ ...prev, [trackId]: pct }));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);
          resolve(response.secure_url);
        } else {
          reject(new Error('Upload failed'));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      // Cloudinary uses 'video' resource type for audio files
      xhr.open('POST', `https://api.cloudinary.com/v1_1/${cloudName}/video/upload`);
      xhr.send(formData);
    });
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check Cloudinary configuration
    if (!cloudName || !uploadPreset) {
      setError('Cloudinary not configured. Please check your environment variables.');
      return;
    }

    // Validate files
    const maxBytes = maxSizeMB * 1024 * 1024;
    const validFiles = [];

    for (const file of files) {
      if (file.size > maxBytes) {
        setError(`${file.name} is too large. Maximum size is ${maxSizeMB}MB`);
        continue;
      }
      // Accept any audio type or common audio extensions
      const isAudio = file.type.startsWith('audio/') ||
        /\.(mp3|wav|ogg|m4a|aac|flac)$/i.test(file.name);
      if (!isAudio) {
        setError(`${file.name} is not an audio file`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    setIsUploading(true);
    setError(null);

    const newTracks = [...tracks];

    for (const file of validFiles) {
      const trackId = `track_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Initialize progress for this file
      setUploadProgress((prev) => ({ ...prev, [trackId]: 0 }));

      try {
        const downloadURL = await uploadToCloudinary(file, trackId);

        // Extract track name from filename (remove extension)
        const trackName = file.name.replace(/\.[^/.]+$/, '').replace(/_/g, ' ');

        newTracks.push({
          id: trackId,
          title: trackName,
          audioUrl: downloadURL,
          fileName: file.name,
          isFree: false,
          price: '',
          order: newTracks.length + 1,
        });
      } catch (err) {
        console.error('Upload error:', err);
        setError(`Failed to upload ${file.name}`);
      }
    }

    onChange(newTracks);
    setIsUploading(false);
    setUploadProgress({});

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const updateTrack = (trackId, field, value) => {
    const updatedTracks = tracks.map((track) =>
      track.id === trackId ? { ...track, [field]: value } : track
    );
    onChange(updatedTracks);
  };

  const removeTrack = (trackId) => {
    const updatedTracks = tracks.filter((track) => track.id !== trackId);
    // Reorder remaining tracks
    updatedTracks.forEach((track, index) => {
      track.order = index + 1;
    });
    onChange(updatedTracks);
  };

  const addManualTrack = () => {
    const newTrack = {
      id: `track_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: '',
      audioUrl: '',
      fileName: '',
      isFree: false,
      price: '',
      order: tracks.length + 1,
    };
    onChange([...tracks, newTrack]);
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div className="relative">
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac,.flac"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="w-full border-2 border-dashed border-gray-700 rounded-lg p-6 hover:border-orange-500/50 transition-colors group disabled:opacity-50"
        >
          <div className="flex flex-col items-center gap-2 text-gray-400 group-hover:text-orange-400 transition-colors">
            {isUploading ? (
              <>
                <Loader2 className="w-8 h-8 animate-spin" />
                <span className="font-medium">Uploading files...</span>
              </>
            ) : (
              <>
                <Upload className="w-8 h-8" />
                <span className="font-medium">Click to upload audio files</span>
                <span className="text-xs text-gray-500">
                  MP3, WAV, OGG • Max {maxSizeMB}MB per file • Select multiple files
                </span>
              </>
            )}
          </div>
        </button>
      </div>

      {/* Upload Progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <div className="space-y-2">
          {Object.entries(uploadProgress).map(([id, progress]) => (
            <div key={id} className="flex items-center gap-3 p-2 bg-gray-800 rounded-lg">
              <Loader2 className="w-4 h-4 text-orange-500 animate-spin" />
              <div className="flex-1">
                <div className="w-full h-1.5 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-orange-500 to-red-500 transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
              <span className="text-xs text-gray-400">{progress}%</span>
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
          <X className="w-4 h-4" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Track List */}
      {tracks.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-medium text-white">
              Tracks ({tracks.length})
            </h5>
            <button
              type="button"
              onClick={addManualTrack}
              className="text-xs text-orange-400 hover:text-orange-300 flex items-center gap-1"
            >
              <Plus className="w-3 h-3" />
              Add manually
            </button>
          </div>

          <AnimatePresence>
            {tracks.map((track, index) => (
              <motion.div
                key={track.id}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-gray-800 rounded-lg p-4 space-y-3"
              >
                {/* Track Header */}
                <div className="flex items-start gap-3">
                  <div className="flex items-center gap-2 text-gray-500">
                    <GripVertical className="w-4 h-4 cursor-move" />
                    <span className="text-sm font-medium w-6">{index + 1}.</span>
                  </div>

                  <div className="flex-1 space-y-2">
                    <Input
                      value={track.title}
                      onChange={(e) => updateTrack(track.id, 'title', e.target.value)}
                      placeholder="Track title"
                      className="bg-gray-700 border-gray-600"
                    />

                    {!track.audioUrl && (
                      <Input
                        value={track.audioUrl}
                        onChange={(e) => updateTrack(track.id, 'audioUrl', e.target.value)}
                        placeholder="Audio URL (or upload above)"
                        className="bg-gray-700 border-gray-600 text-xs"
                      />
                    )}

                    {track.audioUrl && (
                      <div className="flex items-center gap-2 text-xs text-green-400">
                        <Check className="w-3 h-3" />
                        <span className="truncate">{track.fileName || 'Audio uploaded'}</span>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => removeTrack(track.id)}
                    className="p-1.5 text-gray-400 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Track Pricing */}
                <div className="flex items-center gap-4 pl-10">
                  {/* Free Toggle */}
                  <button
                    type="button"
                    onClick={() => updateTrack(track.id, 'isFree', !track.isFree)}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors ${
                      track.isFree
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                        : 'bg-gray-700 text-gray-400 border border-gray-600 hover:border-green-500/50'
                    }`}
                  >
                    <Gift className="w-3 h-3" />
                    {track.isFree ? 'Free' : 'Mark Free'}
                  </button>

                  {/* Price Input */}
                  {!track.isFree && (
                    <div className="flex items-center gap-1.5">
                      <DollarSign className="w-3 h-3 text-gray-400" />
                      <Input
                        value={track.price}
                        onChange={(e) => updateTrack(track.id, 'price', e.target.value)}
                        placeholder="0.99"
                        className="w-20 h-7 text-xs bg-gray-700 border-gray-600"
                      />
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
